(() => {
  const storageKey = 'vspots_geo_position_v2';
  const storageLifetime = 7 * 24 * 60 * 60 * 1000;
  const preferredAccuracy = 5000;
  const maximumAcceptedAccuracy = 10000;
  const locationRequestTimeout = 15000;
  const cardSelector = '[data-vspots-geo-card]';
  const gridSelector = [
    '.vspots-deal-grid',
    '.main-collection .product-grid',
    '.main-collection .resource-list__grid',
    '.search-results .product-grid',
    '.search-results .resource-list__grid',
  ].join(',');
  const categoryAliases = {
    top10: ['top10', 'top-10', 'top-10-deals'],
    food: ['food', 'essen-trinken', 'essen', 'trinken', 'gastronomie'],
    wellness: ['wellness', 'spa', 'sauna'],
    activity: ['activity', 'freizeit-action', 'freizeit', 'action', 'aktivitaet'],
    beauty: ['beauty', 'schoenheit'],
    fitness: ['fitness', 'sport'],
    services: ['services', 'service', 'dienstleistungen'],
  };

  let currentPosition = null;
  let currentSort = 'recommended';
  let activeFilters = {
    savings: 0,
    category: '',
    showSoldOut: false,
  };
  let isApplying = false;
  let lastGridSignature = '';
  let scheduledFrame = null;
  let locationWatchId = null;
  let locationRequestTimer = null;
  let nearbyCatalogPromise = null;
  let nearbyCatalogTemplates = [];

  function getControls() {
    return Array.from(document.querySelectorAll('[data-vspots-geo-control]'));
  }

  function getToolbars() {
    return Array.from(document.querySelectorAll('[data-vspots-deal-tools]'));
  }

  function getGrids() {
    return Array.from(document.querySelectorAll(gridSelector)).filter(
      (grid) => !grid.closest('[data-vspots-api-root]')
    );
  }

  function getGridSignature() {
    return getGrids()
      .flatMap((grid) => Array.from(grid.querySelectorAll(cardSelector)))
      .map((card) => card.dataset.vspotsProductId || card.dataset.productId || '')
      .join('|');
  }

  function readStoredPosition() {
    try {
      const storedPosition = JSON.parse(window.localStorage.getItem(storageKey));
      const isValid =
        Number.isFinite(storedPosition?.latitude) &&
        Number.isFinite(storedPosition?.longitude) &&
        Number.isFinite(storedPosition?.accuracy) &&
        storedPosition.accuracy <= maximumAcceptedAccuracy &&
        Date.now() - storedPosition.createdAt < storageLifetime;

      if (isValid) return storedPosition;
    } catch {
      window.localStorage.removeItem(storageKey);
    }

    return null;
  }

  function savePosition(position) {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          latitude: Number(position.latitude.toFixed(4)),
          longitude: Number(position.longitude.toFixed(4)),
          accuracy: Math.round(position.accuracy),
          createdAt: Date.now(),
        })
      );
    } catch {
      return;
    }
  }

  function distanceInKilometers(origin, destination) {
    const earthRadius = 6371;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const latitudeDelta = toRadians(destination.latitude - origin.latitude);
    const longitudeDelta = toRadians(destination.longitude - origin.longitude);
    const originLatitude = toRadians(origin.latitude);
    const destinationLatitude = toRadians(destination.latitude);
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(originLatitude) * Math.cos(destinationLatitude) * Math.sin(longitudeDelta / 2) ** 2;

    return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  function hasUsableDealCoordinates(latitude, longitude) {
    return (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= 34 &&
      latitude <= 72 &&
      longitude >= -25 &&
      longitude <= 45
    );
  }

  function formatDistance(distance) {
    if (distance < 1) {
      const meters = Math.max(100, Math.round((distance * 1000) / 100) * 100);
      return `${meters} m`;
    }

    return `${new Intl.NumberFormat(document.documentElement.lang || 'de', {
      maximumFractionDigits: distance < 10 ? 1 : 0,
    }).format(distance)} km`;
  }

  function getDistanceLabel(formattedDistance) {
    const template = getControls()[0]?.dataset.distanceLabel || '__DISTANCE__ entfernt';
    return template.replace('__DISTANCE__', formattedDistance);
  }

  function renderDistance(card, distance) {
    const media = card.querySelector('.vspots-deal-card__media, .card-gallery, slideshow-component');
    if (!media) return;

    media.classList.add('vspots-geo-media');
    let badge = media.querySelector(':scope > .vspots-distance-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'vspots-distance-badge';
      media.append(badge);
    }

    const formattedDistance = formatDistance(distance);
    badge.textContent = formattedDistance;
    badge.setAttribute('aria-label', getDistanceLabel(formattedDistance));
  }

  function findCard(item) {
    if (item.matches?.(cardSelector)) return item;
    return item.querySelector?.(cardSelector) || null;
  }

  function numberFromDataset(card, key, fallback = 0) {
    const value = Number(card?.dataset[key]);
    return Number.isFinite(value) ? value : fallback;
  }

  function calculateCardDistance(card) {
    if (!currentPosition || !card) return Number.POSITIVE_INFINITY;

    const latitudeValue = card.dataset.vspotsLatitude;
    const longitudeValue = card.dataset.vspotsLongitude;
    const latitude = Number(latitudeValue);
    const longitude = Number(longitudeValue);
    const hasCoordinates =
      latitudeValue !== undefined &&
      latitudeValue !== '' &&
      longitudeValue !== undefined &&
      longitudeValue !== '' &&
      hasUsableDealCoordinates(latitude, longitude);

    if (!hasCoordinates) return Number.POSITIVE_INFINITY;

    return distanceInKilometers(currentPosition, { latitude, longitude });
  }

  function getDistance(card) {
    const distance = calculateCardDistance(card);
    if (!Number.isFinite(distance)) return distance;

    card.dataset.vspotsDistanceKm = distance.toFixed(3);
    renderDistance(card, distance);
    return distance;
  }

  function categoryMatches(card, selectedCategory) {
    if (!selectedCategory) return true;

    const values = [
      card?.dataset.vspotsCategory,
      card?.dataset.vspotsCollections,
      card?.dataset.vspotsTags,
    ]
      .filter(Boolean)
      .join(',')
      .toLowerCase()
      .replaceAll('_', '-')
      .replaceAll('ä', 'ae')
      .replaceAll('ö', 'oe')
      .replaceAll('ü', 'ue')
      .replaceAll('ß', 'ss');
    const aliases = categoryAliases[selectedCategory] || [selectedCategory];

    return aliases.some((alias) => values.includes(alias));
  }

  function buildEntries(grid) {
    return Array.from(grid.children)
      .filter((item) => !item.matches('[data-vspots-filter-empty]'))
      .map((item, index) => {
        const card = findCard(item);
        if (!card) return null;

        const suppliedRank = numberFromDataset(card, 'vspotsOriginalRank', index);
        if (!item.dataset.vspotsOriginalRank) item.dataset.vspotsOriginalRank = String(suppliedRank);

        const distance = getDistance(card);
        const savings = numberFromDataset(card, 'vspotsDiscount');
        const isAvailable = card.dataset.vspotsAvailable !== 'false';
        const isVisible =
          savings >= activeFilters.savings &&
          categoryMatches(card, activeFilters.category) &&
          (activeFilters.showSoldOut || isAvailable);

        return {
          item,
          card,
          distance,
          price: numberFromDataset(card, 'vspotsPrice'),
          popularity: numberFromDataset(card, 'vspotsPopularity'),
          publishedAt: numberFromDataset(card, 'vspotsPublishedAt'),
          originalRank: numberFromDataset(item, 'vspotsOriginalRank', index),
          isVisible,
        };
      })
      .filter(Boolean);
  }

  function getNearbyHomeContext() {
    const section = document.querySelector('[data-vspots-nearby-catalog-url]');
    const grid = section?.querySelector('[data-vspots-home-deal-grid]');
    if (!section || !grid) return null;

    return {
      section,
      grid,
      catalogUrl: section.dataset.vspotsNearbyCatalogUrl,
      limit: Math.max(1, Number(section.dataset.vspotsNearbyLimit) || 12),
    };
  }

  function parseNearbyCatalogPage(markup) {
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    const page = documentFragment.querySelector('[data-vspots-nearby-page]');
    if (!page) throw new Error('VSPOTS nearby catalog response is incomplete.');

    return {
      pages: Math.max(1, Number(page.dataset.vspotsPages) || 1),
      templates: Array.from(page.querySelectorAll('template[data-vspots-nearby-card]')),
    };
  }

  async function fetchNearbyCatalogPage(catalogUrl, pageNumber) {
    const url = new URL(catalogUrl, window.location.origin);
    url.searchParams.set('page', String(pageNumber));

    const response = await window.fetch(url, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`VSPOTS nearby catalog request failed with ${response.status}.`);

    return parseNearbyCatalogPage(await response.text());
  }

  function loadNearbyCatalog() {
    const context = getNearbyHomeContext();
    if (!context?.catalogUrl) return Promise.resolve([]);
    if (nearbyCatalogTemplates.length > 0) return Promise.resolve(nearbyCatalogTemplates);
    if (nearbyCatalogPromise) return nearbyCatalogPromise;

    nearbyCatalogPromise = fetchNearbyCatalogPage(context.catalogUrl, 1)
      .then(async (firstPage) => {
        const remainingPageNumbers = Array.from(
          { length: Math.max(0, firstPage.pages - 1) },
          (_, index) => index + 2
        );
        const remainingPages = await Promise.all(
          remainingPageNumbers.map((pageNumber) => fetchNearbyCatalogPage(context.catalogUrl, pageNumber))
        );

        nearbyCatalogTemplates = [
          ...firstPage.templates,
          ...remainingPages.flatMap((page) => page.templates),
        ];
        return nearbyCatalogTemplates;
      })
      .catch((error) => {
        nearbyCatalogPromise = null;
        throw error;
      });

    return nearbyCatalogPromise;
  }

  function cardMatchesActiveFilters(card) {
    const savings = numberFromDataset(card, 'vspotsDiscount');
    const isAvailable = card.dataset.vspotsAvailable !== 'false';

    return (
      savings >= activeFilters.savings &&
      categoryMatches(card, activeFilters.category) &&
      (activeFilters.showSoldOut || isAvailable)
    );
  }

  function refreshNearbyHomeGrid() {
    const context = getNearbyHomeContext();
    if (!context || !currentPosition || nearbyCatalogTemplates.length === 0) return false;

    const nearestTemplates = nearbyCatalogTemplates
      .map((template, index) => {
        const card = template.content.querySelector(cardSelector);
        if (!card || !cardMatchesActiveFilters(card)) return null;

        return {
          template,
          distance: calculateCardDistance(card),
          originalRank: numberFromDataset(card, 'vspotsOriginalRank', index),
        };
      })
      .filter((entry) => entry && Number.isFinite(entry.distance))
      .sort((left, right) => left.distance - right.distance || left.originalRank - right.originalRank)
      .slice(0, context.limit);

    if (nearestTemplates.length === 0) return false;

    const fragment = document.createDocumentFragment();
    nearestTemplates.forEach(({ template }) => {
      const card = template.content.querySelector(cardSelector);
      if (card) fragment.append(card.cloneNode(true));
    });

    context.grid.replaceChildren(fragment);
    context.grid.dataset.vspotsNearbyHydrated = 'true';
    return true;
  }

  function compareEntries(left, right) {
    let result = 0;

    if (currentSort === 'distance' && currentPosition) result = left.distance - right.distance;
    if (currentSort === 'date') result = right.publishedAt - left.publishedAt;
    if (currentSort === 'price') result = left.price - right.price;
    if (currentSort === 'price-desc') result = right.price - left.price;

    if (Number.isNaN(result) || result === 0) return left.originalRank - right.originalRank;
    return result < 0 ? -1 : 1;
  }

  function renderEmptyState(grid, isEmpty) {
    let message = grid.querySelector(':scope > [data-vspots-filter-empty]');

    if (!isEmpty) {
      message?.remove();
      return;
    }

    if (!message) {
      message = document.createElement('p');
      message.className = 'vspots-filter-empty';
      message.dataset.vspotsFilterEmpty = 'true';
      message.textContent = getToolbars()[0]?.dataset.vspotsEmptyLabel || 'Keine Deals verfügbar.';
      grid.append(message);
    }
  }

  function applyGridState(grid) {
    const entries = buildEntries(grid);
    if (entries.length === 0) return 0;

    entries.sort(compareEntries);
    const fragment = document.createDocumentFragment();
    let visibleCount = 0;

    entries.forEach(({ item, isVisible, distance }, sortedIndex) => {
      item.style.setProperty('order', String(sortedIndex), 'important');
      item.dataset.vspotsFilterHidden = String(!isVisible);
      if (Number.isFinite(distance)) item.dataset.vspotsDistanceKm = distance.toFixed(3);
      if (isVisible) visibleCount += 1;
      fragment.append(item);
    });

    grid.append(fragment);
    grid.dataset.vspotsSortedBy = currentSort;
    renderEmptyState(grid, visibleCount === 0);
    return visibleCount;
  }

  function setControlState(state) {
    getControls().forEach((control) => {
      const button = control.querySelector('[data-vspots-geo-trigger]');
      const buttonText = control.querySelector('[data-vspots-geo-button-text]');
      const status = control.querySelector('[data-vspots-geo-status]');
      const hasPosition = state === 'active' || state === 'ready';
      const isRequesting = state === 'requesting';
      const statusKey =
        state === 'active'
          ? 'statusSorted'
          : state === 'requesting'
            ? 'statusRequesting'
            : state === 'denied'
              ? 'statusDenied'
              : state === 'imprecise'
                ? 'statusImprecise'
              : state === 'unavailable'
                ? 'statusUnavailable'
                : null;

      control.dataset.state = state;
      button.disabled = isRequesting;
      button.setAttribute('aria-pressed', String(hasPosition));
      buttonText.textContent = hasPosition ? control.dataset.labelActive : control.dataset.labelIdle;

      if (statusKey) {
        status.textContent = control.dataset[statusKey];
        status.hidden = false;
      } else {
        status.textContent = '';
        status.hidden = true;
      }
    });
  }

  function updateFilterCount() {
    const activeCount =
      Number(activeFilters.savings > 0) +
      Number(Boolean(activeFilters.category)) +
      Number(activeFilters.showSoldOut);

    document.querySelectorAll('[data-vspots-filter-count]').forEach((badge) => {
      badge.textContent = String(activeCount);
      badge.hidden = activeCount === 0;
    });
  }

  function syncToolForms() {
    getToolbars().forEach((toolbar) => {
      const sortInput = toolbar.querySelector(`[data-vspots-sort-form] input[value="${currentSort}"]`);
      if (sortInput) sortInput.checked = true;

      const savingsInput = toolbar.querySelector(
        `[data-vspots-filter-form] input[name^="vspots-savings-"][value="${activeFilters.savings}"]`
      );
      const categoryInput = toolbar.querySelector(
        `[data-vspots-filter-form] input[name^="vspots-category-"][value="${activeFilters.category}"]`
      );
      const soldOutValue = activeFilters.showSoldOut ? 'show' : 'hide';
      const soldOutInput = toolbar.querySelector(
        `[data-vspots-filter-form] input[name^="vspots-sold-out-"][value="${soldOutValue}"]`
      );

      if (savingsInput) savingsInput.checked = true;
      if (categoryInput) categoryInput.checked = true;
      if (soldOutInput) soldOutInput.checked = true;
    });

    updateFilterCount();
  }

  function applyCurrentState() {
    if (isApplying) return;
    isApplying = true;

    refreshNearbyHomeGrid();
    getGrids().forEach(applyGridState);

    lastGridSignature = getGridSignature();
    isApplying = false;
    syncToolForms();

    if (currentPosition) setControlState(currentSort === 'distance' ? 'active' : 'ready');
  }

  function applyPosition(position) {
    currentPosition = position;
    currentSort = 'distance';

    if (getNearbyHomeContext()) {
      setControlState('requesting');
      loadNearbyCatalog()
        .catch(() => [])
        .finally(applyCurrentState);
      return;
    }

    applyCurrentState();
  }

  function clearLocationRequest() {
    if (locationWatchId !== null) {
      window.navigator.geolocation.clearWatch(locationWatchId);
      locationWatchId = null;
    }

    if (locationRequestTimer !== null) {
      window.clearTimeout(locationRequestTimer);
      locationRequestTimer = null;
    }
  }

  function requestPosition() {
    if (!window.navigator.geolocation) {
      setControlState('unavailable');
      return;
    }

    clearLocationRequest();
    setControlState('requesting');

    let bestPosition = null;
    let isFinished = false;
    let isFallbackRequesting = false;

    const getPositionCandidate = (coords) => {
      const candidate = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
      };
      const isValid =
        Number.isFinite(candidate.latitude) &&
        Number.isFinite(candidate.longitude) &&
        Number.isFinite(candidate.accuracy);

      return isValid ? candidate : null;
    };

    const finishWithBestPosition = () => {
      if (isFinished) return;
      isFinished = true;
      clearLocationRequest();

      if (!bestPosition) {
        setControlState('unavailable');
        return;
      }

      if (bestPosition.accuracy > maximumAcceptedAccuracy) {
        setControlState('imprecise');
        return;
      }

      savePosition(bestPosition);
      applyPosition(bestPosition);
    };

    const startFallbackRequest = () => {
      if (isFinished || isFallbackRequesting) return;
      isFallbackRequesting = true;
      clearLocationRequest();

      window.navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const candidate = getPositionCandidate(coords);
          if (candidate && (!bestPosition || candidate.accuracy < bestPosition.accuracy)) {
            bestPosition = candidate;
          }
          finishWithBestPosition();
        },
        (error) => {
          if (isFinished) return;
          isFinished = true;
          clearLocationRequest();
          setControlState(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
        },
        {
          enableHighAccuracy: false,
          maximumAge: 0,
          timeout: locationRequestTimeout,
        }
      );
    };

    locationWatchId = window.navigator.geolocation.watchPosition(
      ({ coords }) => {
        const candidate = getPositionCandidate(coords);
        if (!candidate) return;
        if (!bestPosition || candidate.accuracy < bestPosition.accuracy) bestPosition = candidate;
        if (candidate.accuracy <= preferredAccuracy) finishWithBestPosition();
      },
      (error) => {
        if (bestPosition) {
          finishWithBestPosition();
          return;
        }

        if (error.code === error.PERMISSION_DENIED) {
          isFinished = true;
          clearLocationRequest();
          setControlState('denied');
          return;
        }

        startFallbackRequest();
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: locationRequestTimeout,
      }
    );

    locationRequestTimer = window.setTimeout(() => {
      if (bestPosition) {
        finishWithBestPosition();
        return;
      }

      startFallbackRequest();
    }, locationRequestTimeout);
  }

  function closeDialog(dialog) {
    if (!dialog?.open) return;
    dialog.close();
    const trigger = dialog.vspotsTrigger;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }
  }

  function openDialog(toolbar, name, trigger) {
    const dialog = toolbar.querySelector(`[data-vspots-tool-dialog="${name}"]`);
    if (!dialog) return;

    document.querySelectorAll('[data-vspots-tool-dialog][open]').forEach((openDialogElement) => {
      if (openDialogElement !== dialog) closeDialog(openDialogElement);
    });

    dialog.vspotsTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
  }

  function bindControls() {
    getControls().forEach((control) => {
      if (control.closest('[data-vspots-api-root]')) return;
      const button = control.querySelector('[data-vspots-geo-trigger]');
      if (!button || button.dataset.vspotsGeoBound === 'true') return;
      button.dataset.vspotsGeoBound = 'true';
      button.addEventListener('click', requestPosition);
    });
  }

  function bindToolbars() {
    getToolbars().forEach((toolbar) => {
      if (toolbar.closest('[data-vspots-api-root]')) return;
      if (toolbar.dataset.vspotsToolsBound === 'true') return;
      toolbar.dataset.vspotsToolsBound = 'true';

      toolbar.querySelectorAll('[data-vspots-dialog-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', () => openDialog(toolbar, trigger.dataset.vspotsDialogTrigger, trigger));
      });

      toolbar.querySelectorAll('[data-vspots-tool-dialog]').forEach((dialog) => {
        dialog.querySelector('[data-vspots-dialog-close]')?.addEventListener('click', () => closeDialog(dialog));
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) closeDialog(dialog);
        });
      });

      toolbar.querySelector('[data-vspots-sort-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const selectedSort = event.currentTarget.querySelector('input:checked')?.value || 'recommended';
        currentSort = selectedSort;
        closeDialog(event.currentTarget.closest('dialog'));

        if (currentSort === 'distance' && !currentPosition) {
          requestPosition();
          return;
        }

        applyCurrentState();
      });

      toolbar.querySelector('[data-vspots-filter-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        activeFilters = {
          savings: Number(form.querySelector('input[name^="vspots-savings-"]:checked')?.value || 0),
          category: form.querySelector('input[name^="vspots-category-"]:checked')?.value || '',
          showSoldOut: form.querySelector('input[name^="vspots-sold-out-"]:checked')?.value === 'show',
        };
        closeDialog(form.closest('dialog'));
        applyCurrentState();
      });

      toolbar.querySelector('[data-vspots-filter-reset]')?.addEventListener('click', () => {
        activeFilters = { savings: 0, category: '', showSoldOut: false };
        syncToolForms();
        applyCurrentState();
      });
    });
  }

  function normalizeSearchText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('de-DE')
      .trim();
  }

  function getSearchMatches(catalog, query) {
    const normalizedQuery = normalizeSearchText(query);
    if (normalizedQuery.length < 2) return [];

    const tokens = normalizedQuery.split(/\s+/).filter(Boolean);

    return catalog
      .filter((item) => item?.available !== false)
      .map((item) => {
        const title = normalizeSearchText(item.title);
        const city = normalizeSearchText(item.city);
        const postalCode = normalizeSearchText(item.postalCode);
        const vendor = normalizeSearchText(item.vendor);
        const subtitle = normalizeSearchText(item.subtitle);
        const category = normalizeSearchText(item.category);
        const aliases = Array.isArray(item.aliases)
          ? item.aliases.map(normalizeSearchText).join(' ')
          : normalizeSearchText(item.aliases);
        const tags = Array.isArray(item.tags)
          ? item.tags.map(normalizeSearchText).join(' ')
          : normalizeSearchText(item.tags);
        const haystack = [title, city, postalCode, vendor, subtitle, category, aliases, tags].join(' ');
        const matches = tokens.every((token) => haystack.includes(token));

        if (!matches) return null;

        let score = 4;
        if (title.startsWith(normalizedQuery)) score = 0;
        else if (postalCode.startsWith(normalizedQuery)) score = 1;
        else if (city.startsWith(normalizedQuery)) score = 2;
        else if (vendor.startsWith(normalizedQuery)) score = 3;

        return { item, score };
      })
      .filter(Boolean)
      .sort((left, right) => left.score - right.score || left.item.title.localeCompare(right.item.title, 'de'))
      .slice(0, 6)
      .map(({ item }) => item);
  }

  function setSearchResultsVisibility(input, results, isVisible) {
    results.hidden = !isVisible;
    input.setAttribute('aria-expanded', String(isVisible));
  }

  function renderSearchMatches(input, results, catalog) {
    const matches = getSearchMatches(catalog, input.value);
    results.replaceChildren();

    matches.forEach((item) => {
      const link = document.createElement('a');
      link.className = 'vspots-search-result';
      link.href = item.url;
      link.setAttribute('role', 'option');

      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 72;
        image.height = 54;
        image.loading = 'lazy';
        link.append(image);
      } else {
        const imagePlaceholder = document.createElement('span');
        imagePlaceholder.className = 'vspots-search-result__placeholder';
        imagePlaceholder.setAttribute('aria-hidden', 'true');
        link.append(imagePlaceholder);
      }

      const content = document.createElement('span');
      content.className = 'vspots-search-result__content';

      const title = document.createElement('strong');
      title.textContent = item.title;
      content.append(title);

      const location = [item.postalCode, item.city].filter(Boolean).join(' ');
      const details = document.createElement('span');
      details.textContent = [location, item.vendor].filter(Boolean).join(' · ');
      content.append(details);
      link.append(content);

      const price = document.createElement('strong');
      price.className = 'vspots-search-result__price';
      price.textContent = item.price;
      link.append(price);
      results.append(link);
    });

    setSearchResultsVisibility(input, results, matches.length > 0);
    return matches;
  }

  function bindLiveSearch() {
    document.querySelectorAll('[data-vspots-live-search]').forEach((form) => {
      if (form.closest('[data-vspots-api-root]')) return;
      if (form.dataset.vspotsLiveSearchBound === 'true') return;

      const input = form.querySelector('[data-vspots-live-search-input]');
      const results = form.querySelector('[data-vspots-live-search-results]');
      const catalogElement = form.querySelector('[data-vspots-search-catalog]');
      if (!input || !results || !catalogElement) return;

      let catalog = [];
      try {
        catalog = JSON.parse(catalogElement.textContent);
      } catch {
        return;
      }

      form.dataset.vspotsLiveSearchBound = 'true';

      input.addEventListener('input', () => renderSearchMatches(input, results, catalog));
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2) renderSearchMatches(input, results, catalog);
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          setSearchResultsVisibility(input, results, false);
          return;
        }

        if (event.key === 'ArrowDown' && !results.hidden) {
          const firstResult = results.querySelector('a');
          if (firstResult) {
            event.preventDefault();
            firstResult.focus();
          }
        }
      });

      form.addEventListener('submit', (event) => {
        const normalizedQuery = normalizeSearchText(input.value);
        const matches = getSearchMatches(catalog, input.value);
        const exactMatch = matches.find((item) =>
          [item.title, item.city, item.postalCode]
            .map(normalizeSearchText)
            .some((value) => value === normalizedQuery)
        );

        if (exactMatch) {
          event.preventDefault();
          window.location.assign(exactMatch.url);
        }
      });

      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) setSearchResultsVisibility(input, results, false);
      });
    });
  }

  function revealPageControl() {
    const main = document.querySelector('#MainContent');
    const shell = document.querySelector('[data-vspots-geo-shell]');
    const hasHomeToolbar = Boolean(document.querySelector('.vspots-deal-tools--home'));
    const hasApiToolbar = Boolean(document.querySelector('[data-vspots-api-root]'));
    const shouldShowShell =
      /collection|search/.test(main?.dataset.template || '') && !hasHomeToolbar && !hasApiToolbar;

    if (shell) shell.hidden = !shouldShowShell;
  }

  function scheduleApply() {
    if (scheduledFrame) return;
    scheduledFrame = window.requestAnimationFrame(() => {
      scheduledFrame = null;
      applyCurrentState();
    });
  }

  function initialize() {
    bindControls();
    bindToolbars();
    bindLiveSearch();
    revealPageControl();
    syncToolForms();

    const storedPosition = readStoredPosition();
    if (storedPosition) {
      applyPosition(storedPosition);
    } else {
      applyCurrentState();
    }

    const main = document.querySelector('#MainContent');
    if (!main) return;

    const observer = new MutationObserver(() => {
      if (isApplying) return;
      const nextSignature = getGridSignature();
      if (nextSignature === lastGridSignature) return;
      scheduleApply();
    });

    observer.observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
